/**
 * Normalize accumulated message content: detect backend DGUI envelope and
 * convert to frontend multipart shape { parts: ContentPart[] }.
 */

import type { ContentPart, DGUIMessageContent } from '../types/dgui';

interface AgentEnvelope {
  _final_response?: boolean;
  _agui_compatible?: boolean;
  parts?: ContentPart[];
  dgu_components?: Array<{
    type?: string;
    component?: string;
    props?: Record<string, unknown>;
    data?: Record<string, unknown>;
    label?: string;
  }>;
  curated?: {
    sections?: {
      orientation_snapshot?: string;
      salient_highlights?: string[];
      contextual_interpretation?: string;
      guided_exploration_paths?: string[];
    };
  };
}

/**
 * Build narrative text from curated.sections (markdown).
 */
function buildNarrativeText(envelope: AgentEnvelope): string {
  const sections = envelope.curated?.sections;
  if (!sections) return '';

  const lines: string[] = [];
  if (sections.orientation_snapshot) {
    lines.push(sections.orientation_snapshot.trim());
  }
  if (sections.salient_highlights?.length) {
    lines.push('');
    lines.push(sections.salient_highlights.map((h) => `- ${h}`).join('\n'));
  }
  if (sections.contextual_interpretation) {
    lines.push('');
    lines.push(sections.contextual_interpretation.trim());
  }
  if (sections.guided_exploration_paths?.length) {
    lines.push('');
    lines.push('**You might also:**');
    lines.push(sections.guided_exploration_paths.map((p) => `- ${p}`).join('\n'));
  }
  return lines.join('\n').trim();
}

type DGUComponentItem = {
  type?: string;
  component?: string;
  props?: Record<string, unknown>;
  data?: Record<string, unknown>;
  label?: string;
};

/** Map DGU type to frontend component name when component is missing. */
const DGU_TYPE_TO_COMPONENT: Record<string, string> = {
  card: 'InfoCard',
  table: 'Table',
  list: 'List',
  neutral_bar: 'NeutralBar',
  bar_graph: 'BarGraph',
  line_graph: 'LineGraph',
  pie_chart: 'PieChart',
  heat_map: 'HeatMap',
};

/**
 * Convert one dgu_component entry to ContentPart (dgui).
 */
function toDGUIPart(item: DGUComponentItem): ContentPart | null {
  if (!item) return null;
  const typeLower = (item.type ?? '').toLowerCase();
  const component =
    item.component ??
    DGU_TYPE_TO_COMPONENT[typeLower] ??
    (typeLower ? typeLower.charAt(0).toUpperCase() + typeLower.slice(1) : null);
  const props = item.props ?? {
    ...(item.data ?? {}),
    ...(item.label != null ? { title: item.label } : {}),
  };
  if (!component) return null;
  return {
    type: 'dgui',
    schema: { component: String(component), props },
  };
}

/** True if the part is our frontend shape (type "text" or "dgui"), not the agent's content.parts (e.g. { text: "..." }). */
function isValidContentPart(part: unknown): part is ContentPart {
  return (
    typeof part === 'object' &&
    part !== null &&
    'type' in (part as object) &&
    ((part as ContentPart).type === 'text' || (part as ContentPart).type === 'dgui')
  );
}

/**
 * Find the matching closing '}' for the '{' at openIdx (brace counting).
 * Skips braces inside double-quoted strings so we don't close early on } in text.
 */
function findMatchingCloseBrace(str: string, openIdx: number): number {
  let depth = 1;
  for (let i = openIdx + 1; i < str.length; i++) {
    const c = str[i];
    if (c === '"') {
      i = skipString(str, i);
      if (i === -1) return -1;
      continue;
    }
    if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/** Skip a double-quoted string; return index of closing " or -1. */
function skipString(str: string, quoteIdx: number): number {
  for (let i = quoteIdx + 1; i < str.length; i++) {
    if (str[i] === '\\') {
      i++;
      continue;
    }
    if (str[i] === '"') return i;
  }
  return -1;
}

/**
 * Try to parse a JSON object from the string (handles concatenated stream content).
 * Uses the last occurrence of '{"parts":' so we always get the last full parts object
 * when the backend sends multiple deltas (e.g. event #3 and #4 both with {"parts": [...]}).
 * Any text after that object is appended as a final text part.
 */
function extractTrailingPartsJson(str: string): { parts: ContentPart[] } | null {
  const objectStart = '{"parts":';
  const start = str.lastIndexOf(objectStart);
  if (start === -1) {
    const partsKey = '"parts":';
    const idx = str.lastIndexOf(partsKey);
    if (idx === -1) return null;
    let brace = idx - 1;
    while (brace >= 0 && str[brace] !== '{') brace--;
    if (brace < 0) return null;
    const end = findMatchingCloseBrace(str, brace);
    if (end === -1) return null;
    return parsePartsAndTrailing(str, brace, end);
  }
  const end = findMatchingCloseBrace(str, start);
  if (end === -1) return null;
  return parsePartsAndTrailing(str, start, end);
}

function parsePartsAndTrailing(
  str: string,
  start: number,
  end: number
): { parts: ContentPart[] } | null {
  try {
    const parsed = JSON.parse(str.slice(start, end + 1)) as { parts?: unknown[] };
    if (!Array.isArray(parsed.parts) || parsed.parts.length === 0) return null;
    const valid = parsed.parts.filter(isValidContentPart);
    if (valid.length === 0) return null;
    const parts: ContentPart[] = [...valid];
    const trailingText = str.slice(end + 1).trim();
    if (trailingText) {
      parts.push({ type: 'text', text: trailingText });
    }
    return { parts };
  } catch {
    return null;
  }
}

/**
 * Normalize accumulated message content to { parts } for rendering.
 * The backend sends one canonical {"parts": [...]} at turn end (DGUI can appear in any event).
 * Accumulated content may be: "Processing request..." + canonical, or just the canonical.
 * We parse the full envelope if possible, or extract the last {"parts": object + trailing text.
 */
export function normalizeDGUIContent(accumulatedContent: string): DGUIMessageContent | null {
  const trimmed = accumulatedContent?.trim();
  if (!trimmed) return null;

  let envelope: AgentEnvelope;
  try {
    envelope = JSON.parse(trimmed) as AgentEnvelope;
  } catch {
    // Content may be concatenated (e.g. "Processing...Hi!{\"parts\":[...]}"); try trailing parts
    const trailing = extractTrailingPartsJson(trimmed);
    if (trailing) return trailing;
    return null;
  }

  if (!envelope || typeof envelope !== 'object') return null;

  // Already in frontend shape
  if (Array.isArray(envelope.parts) && envelope.parts.length > 0) {
    return { parts: envelope.parts };
  }

  // Agent/backend envelope: build parts from curated + dgu_components
  if (envelope._agui_compatible !== true) return null;

  const parts: ContentPart[] = [];
  const narrative = buildNarrativeText(envelope);
  if (narrative) {
    parts.push({ type: 'text', text: narrative });
  }
  const dguList = envelope.dgu_components;
  if (Array.isArray(dguList)) {
    for (const item of dguList as DGUComponentItem[]) {
      const part = toDGUIPart(item);
      if (part) parts.push(part);
    }
  }

  if (parts.length === 0) return null;
  return { parts };
}
